import axios, { AxiosError, AxiosPromise, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import {
  MagnumStartCaseResponse,
  MagnumFormResponse,
  NewMagnumCase,
  MagnumStartCaseRequestParameters,
} from 'interfaces/Magnum';
import { UpdateInsuredInformation } from 'interfaces/Insured';
import { KeycloakInstanceWrapper } from 'Keycloak/Keycloak';
import * as Sentry from '@sentry/browser';
import HealthDeclaration from '../interfaces/HealthDeclaration';
import HealthDecValidation from '../interfaces/HealthDecValidation';
import Locale from '../interfaces/Locale';
import SignedStatus from '../interfaces/SignedStatus';
import Draft, { DraftDto } from '../interfaces/Draft';
import FinalizeHealthDeclaration from '../interfaces/FinalizeHealthDeclaration';
import Language from '../enums/Language';
import AdminSettingsResponse from '../interfaces/AdminSettings';
import HealthDeclarationUploadedDocuments from '../interfaces/HealthDeclarationUploadedDocuments';
import ProductConfigResponse from '../interfaces/ProductConfig';
import { isBrand } from '../utils/utils';
import Brand from '../enums/Brand';

const isProd = process.env.NODE_ENV === 'production';

const API_URL = '/api/questionnairesurveyor';
const MAGNUM_API_URL = '/api/magnum';
const TAF_PC_API_URL = '/api/premiumcalculator';
const WEFOX_OCCUPATION_API_URL = '/api/occupationservice';
export const MAGNUM_PROXY_API_URL = MAGNUM_API_URL;

const axiosSettings: AxiosRequestConfig = {
  baseURL: API_URL,
};

const magnumAxiosSettings: AxiosRequestConfig = {
  baseURL: MAGNUM_API_URL,
};

const pcAxiosSettings: AxiosRequestConfig = {
  baseURL: TAF_PC_API_URL,
};

const wefoxOccupationAxiosSettings: AxiosRequestConfig = {
  baseURL: WEFOX_OCCUPATION_API_URL,
};

const keyCloakInterceptor = (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
  if (config.url && config.url.includes('public')) {
    return config;
  }

  // eslint-disable-next-line
  config.headers.setAuthorization(`Bearer ${KeycloakInstanceWrapper.getInstance().token}`);
  return config;
};

const axiosInstance = axios.create(axiosSettings);
const magnumAxiosInstance = axios.create(magnumAxiosSettings);
const tafPCInstance = axios.create(pcAxiosSettings);

const wefoxOccupationInstance = axios.create(wefoxOccupationAxiosSettings);

axiosInstance.interceptors?.request?.use(keyCloakInterceptor);
magnumAxiosInstance.interceptors?.request?.use(keyCloakInterceptor);
export function errorHandlerForUnavailableServiceCase(err: AxiosError) {
  if (err.message === 'Network Error' || err.code === '503' || err.message === 'Request failed with status code 503') {
    if (isProd) {
      const tafMaintenancePage = 'https://www.taf.nl/onderhoud';
      // TODO: replace with real value later;
      const wefoxMaintenancePage = 'https://wefox.com/risikolebensversicherung/wartung';
      Sentry.setTag('redirect', 'maintenance-page');
      Sentry.captureException(err);
      window.location.href = isBrand(Brand.wefox) ? wefoxMaintenancePage : tafMaintenancePage;
    }
    console.log('Network Error');
  }
}

function errorHandler(err: AxiosError): never {
  errorHandlerForUnavailableServiceCase(err);
  throw err;
}

const Requests = {
  getHealthDeclaration: (): AxiosPromise<HealthDeclaration | never> => (
    axiosInstance.get<HealthDeclaration>('/health-declarations')
      .catch(errorHandler)
  ),
  getHealthDeclarationDraft: (): AxiosPromise<Draft> => (
    axiosInstance.get<Draft>('/health-declarations/drafts')
      .catch(errorHandler)
  ),
  postHealthDeclarationDraft: (data: DraftDto): AxiosPromise<Draft> => (
    axiosInstance.post<Draft>('/health-declarations/drafts', data)
      .catch(errorHandler)
  ),
  deleteHealthDeclarationDraft: (): AxiosPromise<Draft> => (
    axiosInstance.delete<Draft>('/health-declarations/drafts')
      .catch(errorHandler)
  ),
  rejectHealthDeclaration: (): AxiosPromise => (
    axiosInstance.put('/health-declarations/rejected')
      .catch(errorHandler)
  ),
  signHealthDeclaration: (): AxiosPromise => (
    axiosInstance.post('/health-declarations/signed')
      .catch(errorHandler)
  ),
  reopenHealthDeclaration: (): AxiosPromise => (
    axiosInstance.put('/health-declarations/reopen')
      .catch(errorHandler)
  ),
  getHealthDeclarationStatus: (healthDeclarationId: string): AxiosPromise<HealthDecValidation> => (
    axiosInstance.get<HealthDecValidation>(`/public/health-declarations/${healthDeclarationId}/validate`)
      .catch(errorHandler)
  ),
  getSignStatus: (): AxiosPromise<SignedStatus> => (
    axiosInstance.get<SignedStatus>('/health-declarations/sign/status')
      .catch(errorHandler)
  ),
  updateInsuredInfo: (insured: UpdateInsuredInformation): AxiosPromise => (
    axiosInstance.patch('/users', insured)
      .catch(errorHandler)
  ),
  getLocale: (): AxiosPromise<Locale> => (
    axiosInstance.get<Locale>('/users/locale')
      .catch(errorHandler)
  ),
  saveLocale: (locale: Language): AxiosPromise => (
    axiosInstance.put(`/users/locale/${locale}`)
      .catch(errorHandler)
  ),
  finalizeHealthDeclaration: (data: FinalizeHealthDeclaration): AxiosPromise<HealthDeclaration> => (
    axiosInstance.post<HealthDeclaration>('/health-declarations/finalize', data)
      .catch(errorHandler)
  ),
  isSignedDocAvailable: (): AxiosPromise => (
    axiosInstance.head('/health-declarations/signed-document')
      .catch(errorHandler)
  ),
  downloadSignedDoc: (): AxiosPromise => (
    axiosInstance.get('/health-declarations/signed-document', {
      responseType: 'blob',
    })
      .catch(errorHandler)
  ),
  isMedicalReasonDocAvailable: (): AxiosPromise => (
    axiosInstance.head('/health-declarations/medical-explanation-document')
      .catch(errorHandler)
  ),
  downloadMedicalReasonDoc: (): AxiosPromise => (
    axiosInstance.get('/health-declarations/medical-explanation-document', {
      responseType: 'blob',
    })
      .catch(errorHandler)
  ),
  uploadDocuments: (formData: FormData): AxiosPromise => (
    axiosInstance.post('/health-declarations/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Brand': localStorage.getItem('brand'),
      },
    })
      .catch(errorHandler)
  ),
  getUploadedDocuments: (): AxiosPromise<HealthDeclarationUploadedDocuments> => (
    axiosInstance.get<HealthDeclarationUploadedDocuments>('/health-declarations/upload')
      .catch(errorHandler)
  ),
  magnum: {
    getCaseID: (data: MagnumStartCaseRequestParameters): AxiosPromise<MagnumStartCaseResponse> => (
      magnumAxiosInstance.post<MagnumStartCaseResponse>('secured/retrieve-case', data)
        .catch(errorHandler)
    ),
    reinitialize: (data: MagnumStartCaseRequestParameters): AxiosPromise<MagnumStartCaseResponse> => (
      magnumAxiosInstance.post<MagnumStartCaseResponse>('secured/reinitialize', data)
        .catch(errorHandler)
    ),
  },
  reflex: {
    initRequest: (data: any): AxiosPromise => (
      axiosInstance.post('/reflex/secured/initialize', data).catch(errorHandler)),
  },
  getAdminSetting: (): AxiosPromise<AdminSettingsResponse> => (
    axiosInstance.get<AdminSettingsResponse>('/admin/settings')
      .catch(errorHandler)
  ),
  getProductConfig: (): AxiosPromise<ProductConfigResponse> => (
    axiosInstance.get<ProductConfigResponse>('/product-config')
      .catch(errorHandler)
  ),
  getPublicProductConfig: (healthDeclarationId: string): AxiosPromise<ProductConfigResponse> => (
    axiosInstance.get<ProductConfigResponse>(`/public/product-config/${healthDeclarationId}`)
      .catch(errorHandler)
  ),
};

export default Requests;

export {
  axiosInstance,
  magnumAxiosInstance,
  tafPCInstance,
  wefoxOccupationInstance,
};
